<template>
    <div class="message-area">
        <MessageAreaHeader
            :mode="conversationMode"
            :superMenuState="superMenuState"
            @command="setMessage"
            @modeChanged="modeChanged"
            @updateSuperMenuState="updateSuperMenuState"
            @usedSlashCommand="usedSlashCommand"
            @send="sendTemplateMessage"
            :contact="contact"
        />
        <div class="message-area-container">
            <InformationOverlay
                :key="contact._id"
                :status="contactStatus"
                :contact="contact"
                :instagram-reply-mode="instagramReplyMode"
                :reply-to-message="getReplyToMessage()"
                @focusEditor="focusEditor"
                @blurEditor="blurEditor"
                @send-template-message="triggerTemplateMessage"
                @reply-comment-as-dm="handleReplyCommentAsDM"
                @reply-comment="handleReplyComment"
                @toggle-hide-comment="handleToggleHideComment"
                @delete-comment="handleDeleteComment"
            />
            <MailSubject v-model="mailSubject" v-if="conversationMode === 'email'" />
            <SMSInput :value="smsInput" @input="(input) => (smsInput = input)" v-if="conversationMode === 'sms'" />
            <ChatEditor
                ref="editor"
                v-model="message"
                :file-send="!['sms', 'email'].includes(conversationMode)"
                :is-text="conversationMode === 'sms'"
                :enabled-banned-words="true"
                :emoji-picker="true"
                :speech-recognition="true"
                :share-voice="shareVoiceEnabled()"
                :share-location="shareLocationEnabled()"
                :share-attachment="true"
                :placeholder="generateChatSuggestions()"
                @ai-tools="changeWithAI"
                @updateSuperMenuState="updateSuperMenuState"
                @submit="submitForm"
                @location="sendLocation"
                @voice-message="sendVoiceMessage"
                @media-message="$emit('media-message', $event)"
                @upload-file="$emit('upload-file')"
                @open-google-drive-picker="showGoogleDrivePicker = true"
                :editable="!contactStatus"
            :accepted-files="getAcceptedFiles()"
        />
            <GoogleDriveFilePicker
                v-if="showGoogleDrivePicker"
                @cancel="showGoogleDrivePicker = false"
                @confirm="handleGoogleDriveFiles"
            />
        </div>
    </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";
import LocationShare from "../../../../LocationShare.vue";
import SendIcon from "../../../../svg/SendIcon.vue";
import VoiceRecorder from "../../../../VoiceRecorder.vue";
import MicButton from "../../../../../utils/speech/MicButton.vue";
import CoPilotButton from "../../../../ui/radial-menu/CoPilotButton.vue";
import EmojiButton from "../../../../EmojiButton.vue";
import _ from "lodash";
import CKEditor from "@ckeditor/ckeditor5-vue";
import TiptapEditor from "../../../../../views/Pages/Chatbot/pages/dashboard/common/TiptapEditor.vue";
import ChatEditor from "./Editor.vue";
import RadialMenu from "../../../../ui/radial-menu/RadialMenu.vue";
import systemPubSub from "../../../../../utils/systemPubSub";
import { log2 } from "../../../../../utils/log2";
import { filter, first, map } from "rxjs/operators";
import { sendSocketMessage, socketMessageStream } from "../../../../../utils/socketStream";
import { generateUUID } from "../../../../../utils/session";
import MessageAreaHeader from "./MessageAreaHeader.vue";
import SMSInput from "./SMSInput.vue";
import MailSubject from "./MailSubject.vue";
import axios from "axios";
import { uploadAsset } from "../../../../../api/assetUpload";
import getRandomShortcut from "../../../../../utils/shortcutAll";
import InformationOverlay from "./InformationOverlay.vue";
import StringUtils from "../../../../../utils/StringUtils";
import GoogleDriveFilePicker from "../../../../google-drive/GoogleDriveFilePicker.vue";
import TemplateMessages from "./super-menu/TemplateMessages.vue";

const log = log2("MessageArea");
export default {
    name: "MessageArea",
    components: {
        TemplateMessages,
        InformationOverlay,
        MailSubject,
        SMSInput,
        MessageAreaHeader,
        RadialMenu,
        ChatEditor,
        TiptapEditor,
        CoPilotButton,
        MicButton,
        VoiceRecorder,
        SendIcon,
        LocationShare,
        EmojiButton,
        CKEditor,
        GoogleDriveFilePicker,
    },
    props: {
        contact: {
            type: Object,
            default: () => ({}),
        },
        messageSyncRunning: {
            type: Boolean,
            default: false,
        },
        radialMenuOpened: {
            type: Boolean,
            default: false,
        },
        dragMode: {
            type: Boolean,
            default: false,
        },
        messages: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        ...mapState({
            smsChannels: (state) => state.channel.channels.filter((x) => x.type === "sms-postacell"),
            customerLocationEnabled: (state) => _.get(state, "setting.settings.customerLocationEnabled", true),
            agentFileSendEnabled: (state) => _.get(state, "setting.settings.agentFileSendEnabled", true),
            agentVoiceRecordEnabled: (state) => _.get(state, "setting.settings.agentVoiceRecordEnabled", true),
            allMailAddresses: (state) => state.mail.allMailAddresses,
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
            settings: (state) => state.setting.settings,
            draftMessages: (state) => state.chat.draftMessages,
            liveVisitors: (state) => state.visitor.liveVisitors,
            watchingContactIds: (state) => state.chat.watchingContactIds,
            whatsappBusinessChannels: (state) =>
                state.channel.channels.filter((x) => x.type === "whatsapp-business-api" || x.type === "whatsapp-business-api-supsis"),
            whatsappContactStatus: (state) => state.contact.whatsappContactStatus,
            replyToMessages: (state) => state.chat.pinnedReplyToMessages,
        }),
        ...mapGetters({
            isWhatsappHealthy: "whatsapp/isHealthy",
        }),
        contactStatus() {
            // Instagram/Messenger reply mode aktifse overlay'i kaldır
            if (this.instagramReplyMode) {
                return null;
            }
            
            const isWebPlatform = this.contact.platform === "web";
            const isClosed = this.contact.lastConversation?.status === "destroyed";
            const isLiveVisitor = !!this.liveVisitors.includes(this.contact._id);
            const isAssignMe = this.contact.lastConversation?.users?.includes(this.user._id);
            const isActiveUser = this.isActiveUser();
            if (isClosed) return "enter-create";
            else if (!isAssignMe) return "enter-join";
            if (!isActiveUser) return "deactive-user";
            // /**
            //  * Web Visitor Kuralları
            //  * 1. Eğer Kullanıcı Aktif ve Chat Destroy Olmuş ise Chat Başlatma Aktiftir.
            //  * 2. Eğer Kullanıcı Aktif, Chat Aktif ve Chat içinde Temsilci yoksa Chat Katıl Aktiftir.
            //  * 3. Eğer Kullanıcı Offline ve Chat Destroy Olmuş ise Inactive Chattir
            //  *
            //  * Other Platforms
            //  * 1. Eğer Chat Destroy ise Chat Başlatma Aktiftir.
            //  * 2. Eğer Chat Aktif ve Temsilci Yoksa Chat Katıl
            //  */
            //
            // if (isWebPlatform) {
            //     if (isLiveVisitor && isClosed) return "enter-create";
            //     if (isLiveVisitor && !isClosed && !isAssignMe) return "enter-join";
            //     if (!isLiveVisitor && isClosed) return "inactive";
            // } else {
            //     if (isClosed) return "enter-create";
            //     else if (!isAssignMe) return "enter-join";
            // }
            return null;
        },
    },
    data() {
        return {
            message: "",
            mailSubject: "",
            smsInput: {},
            aiChangeText: null,
            lastMessageUpdateTS: null,
            conversationMode: "chat",
            isSendBtnActive: true,
            speechRecognitionActive: false,
            bannedWordDetected: false,
            superMenuState: {
                enabled: false,
            },
            draftMessageTimers: {},
            cancelTokens: [],
            visitorTyping: null,
            instagramReplyMode: null, // 'dm' veya 'comment'
            showGoogleDrivePicker: false,
        };
    },
    mounted() {
        const readyContent = systemPubSub.stream
            .pipe(filter((x) => x.type === "ready_content_used"))
            .subscribe((data) => {
                const { content, contact } = data.payload || {};
                if (contact._id !== this.contact._id) return;
                this.$refs.editor.setContent(content);
            });
        const focusContent = systemPubSub.stream.pipe(filter((x) => x.type === "focus_content")).subscribe(() => {
            this.focusEditor();
        });
        this.cancelTokens.push(() => {
            readyContent.unsubscribe();
            focusContent.unsubscribe();
        });
        this.bindDraftMessage();
    },
    methods: {
        ...mapActions({
            addPendingMessage: "chat/addPendingMessage",
            removeDraftMessage: "chat/removeDraftMessage",
            createMailAddress: "mail/createMailAddress",
            addDraftMessage: "chat/addDraftMessage",
            unpinReplyToMessage: "chat/unpinReplyToMessage",
        }),
        getAcceptedFiles() {
            const isBusinessChannel = this.whatsappBusinessChannels.find(
                (channel) => channel._id === this.contact.channel
            );
            const isInstagramPlatform = this.contact.platform === "instagram";
            if (isBusinessChannel) {
                // todo ses olayı düzeltildiğinde türler açılacak
                return [
                    /* "audio/aac",
                    "audio/amr",
                    "audio/mpeg",
                    "audio/mp4",
                    "audio/ogg",*/
                    "text/plain",
                    "application/vnd.ms-excel",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    "application/msword",
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                    "application/vnd.ms-powerpoint",
                    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
                    "application/pdf",
                    "image/jpeg",
                    "image/png",
                    "image/jpg",
                    "video/3gp",
                    "video/mp4",
                ];
            }

            if (isInstagramPlatform) {
                // todo ses olayı düzeltildiğinde türler açılacak
                return [
                    /*"audio/aac",
                    "audio/amr",
                    "audio/mpeg",
                    "audio/mp4",
                    "audio/ogg",*/
                    "image/jpeg",
                    "image/png",
                    "image/jpg",
                    "video/3gp",
                    "video/mp4",
                ];
            }
        },
        shareVoiceEnabled() {
            // // todo ses olayı düzeltildiğinde türler açılacak Instagram/Waba/Messenger
            // const isWabaChannel = this.whatsappBusinessChannels.find((channel) => channel._id === this.contact.channel);
            // if (["instagram", "messenger"].includes(this.contact.platform) || isWabaChannel) {
            //     return false;
            // }
            return true;
        },
        shareLocationEnabled() {
            // todo bu kısımlarda location düzeltilince açılacak
            if (["instagram", "messenger", "linkedin"].includes(this.contact.platform)) {
                return false;
            }
            return true;
        },
        getReplyToMessage() {
            return Object.values(this.replyToMessages).find(
                (m) => m.raw.visitorId === this.contact._id || m.raw.visitor === this.contact._id
            );
        },
        triggerTemplateMessage() {
            this.$refs.editor.openReadyContents("template-messages");
        },
        usedSlashCommand(response) {
            if (response.command === "pm" && response.value.length > 0) {
                const replyToMessage = this.$_.get(
                    this.replyToMessages,
                    `${this.contact.lastConversation._id}.raw`,
                    null
                );
                sendSocketMessage("user private-message", {
                    conversationId: this.contact.lastConversation._id,
                    userId: this.user._id,
                    username: this.user.name,
                    message: response.value,
                    siteId: this.site._id,
                    replyToMessage,
                });
                this.unpinReplyToMessage(this.contact.lastConversation._id);
            }
        },
        focusEditor() {
            this.$refs.editor.focus();
        },
        blurEditor() {
            this.$refs.editor.blur();
        },
        generateChatSuggestions() {
            const shortcut = getRandomShortcut();
            return `💡 ${shortcut[this.$i18n.locale] || shortcut.tr}`;
        },
        moveMessageAreaScrollToEnd() {
            const scMessageList = document.querySelector("#chat-messages > .sc-message-list");
            if (scMessageList) {
                scMessageList.scroll(scMessageList.scrollWidth, scMessageList.scrollHeight);
            }
        },
        bindDraftMessage(conversationId) {
            const draftMessage = this.draftMessages[conversationId || this.contact.lastConversation._id];
            this.$refs.editor.setContent(draftMessage || "");
        },
        modeChanged(mode) {
            this.conversationMode = mode;
        },
        setMessage(val) {
            this.$refs.editor.setContent(val);
        },
        submitForm(message) {
            if (this.conversationMode === "chat") {
                this.sendMessage(message);
            } else if (this.conversationMode === "email") {
                this.sendMail(message);
            } else if (this.conversationMode === "sms") {
                this.sendSMS({
                    text: message,
                    ...this.smsInput,
                });
            }
        },
        sendTemplateMessage({ template, params }) {
            this.sendMessage("", template, params);
        },
        isActiveUser() {
            const channel = this.whatsappBusinessChannels.find((channel) => channel._id === this.contact.channel);
            const channelType = _.get(channel, "type", null);
            
            // WhatsApp kontrolü
            if (channelType === "whatsapp-business-api" || channelType === "whatsapp-business-api-supsis") {
                let lastWhatsappBusinessActivityTS = _.get(this.contact, "lastWhatsappBusinessActivityTS");
                lastWhatsappBusinessActivityTS = lastWhatsappBusinessActivityTS ? new Date(lastWhatsappBusinessActivityTS).getTime() : null;
                let whatsappNumber =
                    _.get(this.contact, "lastConversation.whatsappNumber", "") || _.get(this.contact, "whatsappNumber", "");
                let otherNumber = _.get(this.contact, "whatsappNumber", "");
                whatsappNumber = (whatsappNumber || "").replace(/[^0-9]/g, "");
                otherNumber = (otherNumber || "").replace(/[^0-9]/g, "");
                const isIn24Hours =
                    lastWhatsappBusinessActivityTS && Date.now() - lastWhatsappBusinessActivityTS < 24 * 60 * 60 * 1000;
                const isUserActive =
                    this.whatsappContactStatus[`${this.contact.channel}_${whatsappNumber}`] ||
                    this.whatsappContactStatus[`${this.contact.channel}_${otherNumber}`] ||
                    isIn24Hours;
                return !!channel && !!isUserActive;
            }
            
            // Instagram / Messenger kontrolü
            if (this.contact.platform === "instagram" || this.contact.platform === "messenger") {
                const infoField = this.contact.platform === "instagram" ? "instagramInfo" : "messengerInfo";
                const source = _.get(this.contact, `${infoField}.source`);
                const lastMessageTS = _.get(this.contact, `${infoField}.lastMessageTS`);
                const lastMessageSource = _.get(this.contact, `${infoField}.lastMessageSource`);
                const lastCommentReplied = _.get(this.contact, `${infoField}.lastCommentReplied`);
                
                // Eski müşteriler için: source ve lastMessageTS yoksa serbest bırak
                if (!source && !lastMessageTS) {
                    return true;
                }
                
                // Eğer son mesaj tarihi yoksa aktif değil
                if (!lastMessageTS) {
                    return false;
                }
                
                const isIn24Hours = Date.now() - new Date(lastMessageTS).getTime() < 24 * 60 * 60 * 1000;
                
                // 24 saat içindeyse aktif (comment olsa bile)
                if (isIn24Hours) {
                    return true;
                }
                
                // 24 saat dolmuşsa aktif değil
                return false;
            }
            
            return true;
        },
        async sendMessage(message, template, templateParams) {
            const isInstagramOrMessenger = ["instagram", "messenger"].includes(this.contact?.platform);
            
            if (!this.isInputAllowed()) {
                const message = {
                    en: "You are not allowed to send message. Please check whatsapp integration",
                    tr: "Bu mesajı gönderemezseniz whatsapp entegrasyonunu kontrol edin",
                    ru: "Вы не можете отправить сообщение. Пожалуйста, проверьте интеграцию whatsapp",
                    de: "Wenn Sie diese Nachricht nicht senden können, überprüfen Sie die WhatsApp-Integration",
                    fr: "Si vous ne pouvez pas envoyer ce message, vérifiez l'intégration WhatsApp",
                    es: "Si no puede enviar este mensaje, verifique la integración de WhatsApp",
                    ar: "إذا لم تتمكن من إرسال هذه الرسالة ، فتحقق من تكامل whatsapp",
                    it: "Se non puoi inviare questo messaggio controlla l'integrazione di whatsapp",
                };
                this.$toasted.error(message[this.$i18n.locale], {
                    duration: 2000,
                    position: "bottom-center",
                });
                return;
            }
            const replyToMessage = Object.values(this.replyToMessages).find(
                (m) => m.raw.visitorId === this.contact._id || m.raw.visitor === this.contact._id
            );

            message = (typeof message === "string" && message) || this.message;

            if (message.trim().length || template) {
                let clearedMessage = message
                    .trim()
                    .replace(/↵/g, "\n")
                    .replace(/^(\s+<br( \/)?>)*|(<br( \/)?>\s)*$/gm, "")
                    .replace(/<a (.*)>/g, '<a target="_blank" $1>');

                for (const m of ["@name", "@email"]) {
                    let val = m === "@name" ? this.contact.fullname : this.contact.email;
                    clearedMessage = clearedMessage.replace(new RegExp(m, "g"), val);
                }

                let pmMessage = "";
                if (
                    _.take(message.replaceAll(`<p>&nbsp;</p>`, "").replace(/(<([^>]+)>)/gi, ""), 10)
                        .join("")
                        .startsWith("pm:")
                ) {
                    pmMessage = message.trim().replace("pm:", "");
                }

                // Instagram/Messenger reply mode kontrolü - comment veya dm mode için
                if (isInstagramOrMessenger && this.instagramReplyMode) {
                    const platform = this.contact?.platform;
                    const infoField = platform === "instagram" ? "instagramInfo" : "messengerInfo";
                    
                    // Reply to message'dan commentId'yi al veya contact'tan al
                    let commentId = null;
                    const replyToMessageCommentId = _.get(replyToMessage, "raw.externalProviderMessageId") || _.get(replyToMessage, "raw.metaMessageId");
                    if (replyToMessageCommentId) {
                        commentId = replyToMessageCommentId;
                    } else {
                        commentId = _.get(this.contact, `${infoField}.lastCommentId`);
                    }
                    
                    // Comment veya DM olarak cevapla
                    if (commentId) {
                        const isPrivate = this.instagramReplyMode === 'dm';
                        sendSocketMessage("meta reply-comment", {
                            userId: this.user._id,
                            siteId: this.site._id,
                            channelId: this.contact.channel,
                            commentId: commentId,
                            message: clearedMessage,
                            isPrivate: isPrivate,
                            type: platform === "instagram" ? "instagram-comment" : "page-comment",
                        });
                    }
                    this.instagramReplyMode = null;
                    this.message = "";
                    
                    // Reply to message'ı temizle
                    if (replyToMessage) {
                        const conversationId = _.get(
                            replyToMessage,
                            "raw.conversationId",
                            _.get(replyToMessage, "raw.conversation")
                        );
                        this.unpinReplyToMessage(conversationId);
                    }
                    
                    return;
                }

                if (pmMessage.length > 0) {
                    sendSocketMessage("user private-message", {
                        conversationId: this.contact.lastConversation._id,
                        userId: this.user._id,
                        username: this.user.name,
                        message: pmMessage,
                        siteId: this.site._id,
                        replyToMessage: _.get(replyToMessage, "raw"),
                    });
                } else {
                    let payload = {
                        conversationId: this.contact.lastConversation._id,
                        visitorId: this.contact._id,
                        userId: this.user._id,
                        username: this.user.name,
                        message: clearedMessage,
                        siteId: this.site._id,
                        channelId: _.get(this.contact, "lastConversation.channel", null),
                        whatsappNumber:
                            _.get(this.contact, "lastConversation.whatsappNumber", null) ||
                            _.get(this.contact, "whatsappNumber", null),
                        whatsappLid:
                            _.get(this.contact, "whatsappLid", null),
                        instagramUserId:
                            _.get(this.contact, "lastConversation.instagramUserId", null) ||
                            _.get(this.contact, "instagramUserId", null),
                        messengerUserId:
                            _.get(this.contact, "lastConversation.messengerUserId", null) ||
                            _.get(this.contact, "messengerUserId", null),
                        telegramUserId:
                            _.get(this.contact, "lastConversation.telegramUserId", null) ||
                            _.get(this.contact, "telegramUserId", null),
                        linkedinUserId:
                            _.get(this.contact, "lastConversation.linkedinUserId", null) ||
                            _.get(this.contact, "linkedinUserId", null),
                        uniqMessageId: generateUUID(),
                        replyToMessage: _.get(replyToMessage, "raw"),
                        template,
                        templateParams,
                    };
                    const channel = this.whatsappBusinessChannels.find(
                        (channel) => channel._id === this.contact.channel
                    );
                    const isActiveUser = this.isActiveUser();
                    if (template) {
                        payload.messageType = "whatsapp-business-api-template";
                        payload.payload = Object.assign({}, template, {
                            params: StringUtils.clearHTML(templateParams),
                        });
                    } else if (!isActiveUser) {
                        payload.messageType = "whatsapp-business-api-template";
                        payload.payload = Object.assign({}, _.get(channel, "payload.initialTemplate"), {
                            params: StringUtils.clearHTML(message),
                        });
                    }  
                    sendSocketMessage("user message", payload);
                    this.$emit("message", {
                        ...payload,
                        messageType: "user"
                    })
                    this.addPendingMessage(payload);
                }

                this.message = "";
                const conversationId = _.get(
                    replyToMessage,
                    "raw.conversationId",
                    _.get(replyToMessage, "raw.conversation")
                );
                this.unpinReplyToMessage(conversationId);
                setTimeout(() => {
                    this.removeDraftMessage(this.contact.lastConversation._id);
                }, 100);
            }

            this.moveMessageAreaScrollToEnd();
        },
        async sendMail(message) {
            if (message.length === 0) {
                this.$message.error("Mesaj alanı boş geçilemez");
            } else if (this.mailSubject.length === 0) {
                this.$message.error("Subject alanı boş geçilemez");
            } else if (!this.contact.email || this.contact.email.length === 0) {
                this.$message.error("Kullanıcı mail adresi bulunmamaktadır");
            } else {
                const conversation = this.contact.lastConversation;
                this.$message.warning("Mail gönderiliyor...");
                this.createMailAddress({
                    siteId: this.site._id,
                    mail: this.contact.email,
                    type: "visitor",
                    visitorId: this.contact._id,
                });
                const userMailAddress = this.allMailAddresses.filter(
                    (x) => !!x.user && x.user._id === this.user._id && x.isDefault === true
                );
                const regex = /(<([^>]+)>)/gi;
                const textMessage = message.replace(regex, "");
                const data = {
                    ref_visitor_id: this.contact._id,
                    to_list: [this.contact.email],
                    sender: userMailAddress[0].mail,
                    ref_user_ids: [this.user._id],
                    senderName: this.user.name,
                    body_html: message,
                    body_text: textMessage,
                    subject: this.mailSubject,
                    attachments: [],
                    fileNames: [],
                    files: [],
                };
                const requestId = generateUUID();
                const emailPromise = this.waitMail(requestId);
                this.isSendBtnActive = false;
                sendSocketMessage("email send", {
                    siteId: this.site._id,
                    userId: this.user._id,
                    requestId,
                    data,
                });
                const email = await emailPromise;
                this.isSendBtnActive = true;
                let payload = {
                    conversationId: conversation._id,
                    mail: email,
                    username: this.user.name,
                    userId: this.user._id,
                    siteId: this.site._id,
                    messageType: "user-email",
                    message: "",
                    channelId: _.get(conversation, "channel", null),
                    whatsappNumber: _.get(conversation, "whatsappNumber", null),
                    instagramUserId: _.get(conversation, "instagramUserId", null),
                    messengerUserId: _.get(conversation, "messengerUserId", null),
                    telegramUserId: _.get(conversation, "telegramUserId", null),
                    linkedinUserId: _.get(conversation, "linkedinUserId", null),

                    uniqMessageId: generateUUID(),
                };
                this.addPendingMessage(payload);
                sendSocketMessage("user mail", payload);
                this.mailSubject = "";
            }
            this.moveMessageAreaScrollToEnd();
        },
        async sendSMS({ channelId, senderId, countryCode, number }) {
            if (!channelId || !senderId || !number) {
                this.$message.error("Lütfen gerekli yerleri doldurunuz."); // TODO: translate
                return;
            }

            const payload = {
                siteId: this.site._id,
                channelId,
                channelType: "sms-postacell",
                number: countryCode + number,
                message: this.message,
                senderId,
                title: null,
                customMessageId: generateUUID(),
            };
            sendSocketMessage("sms send-single", payload);
            const conversation = this.contact.lastConversation;
            let data = {
                conversationId: conversation._id,
                userId: this.user._id,
                username: this.user.name,
                siteId: this.site._id,
                messageType: "user-sms",
                message: this.message,
                channelId: channelId,
                whatsappNumber: _.get(conversation, "whatsappNumber", null),
                instagramUserId: _.get(conversation, "instagramUserId", null),
                messengerUserId: _.get(conversation, "messengerUserId", null),
                telegramUserId: _.get(conversation, "telegramUserId", null),
                linkedinUserId: _.get(conversation, "linkedinUserId", null),
                uniqMessageId: generateUUID(),
            };
            this.addPendingMessage(data);
            sendSocketMessage("user sms", data);
            this.moveMessageAreaScrollToEnd();
        },
        changeWithAI(payload) {
            this.$emit(
                "ai-copilot",
                payload && {
                    position: { from: payload.from, to: payload.to },
                    message: payload.text,
                    selectionMode: payload.selectionMode,
                }
            );
        },
        isInputAllowed() {
            const isWhatsappChat = !!this.contact.lastConversation.whatsappNumber;
            if (isWhatsappChat) {
                return true || this.isWhatsappHealthy;
            }
            return true;
        },
        updateSuperMenuState(payload) {
            this.superMenuState = payload;
        },
        waitMail(requestId) {
            return socketMessageStream
                .pipe(
                    first((x) => x.cmd === "email_send_successful" && x.data.requestId === requestId),
                    map((x) => x.data.email)
                )
                .toPromise();
        },
        sendTypingInfo: _.debounce(function () {
            if (this.message.length) {
                if (!!this.contact.lastConversation){
                    const lastWabaMessage = _.chain(this.messages).filter(x=>!!_.get(x, "raw.whatsappBusinessMessageId", null)  ).last().value();
                    sendSocketMessage("user typing message", {
                        conversationId: this.contact.lastConversation._id,
                        userId: this.user._id,
                        username: this.user.name,
                        message: "rep-typing",
                        siteId: this.site._id,
                        channelId: _.get(this.contact, "lastConversation.channel", null),
                        whatsappNumber:
                            _.get(this.contact, "lastConversation.whatsappNumber", null) ||
                            _.get(this.contact, "whatsappNumber", null),
                        instagramUserId: _.get(this.contact, "lastConversation.instagramUserId", null),
                        messengerUserId: _.get(this.contact, "lastConversation.messengerUserId", null),
                        telegramUserId:
                            _.get(this.contact, "lastConversation.telegramUserId", null) ||
                            _.get(this.contact, "telegramUserId", null),
                        linkedinUserId: _.get(this.contact, "linkedinUserId", null),
                        lastWabaMessageId: _.get(lastWabaMessage, "raw.whatsappBusinessMessageId", null),
                    });
                }
            }
        }, 500),
        addDraftMessageDelayed(conversationId, message, interval = 1000) {
            let timerId = this.draftMessageTimers[conversationId];
            if (timerId) clearTimeout(timerId);
            timerId = setTimeout(() => {
                this.addDraftMessage({
                    conversationId,
                    message,
                });
            }, interval);
            this.cancelTokens.push(() => clearTimeout(timerId));
            if (timerId) this.draftMessageTimers[conversationId] = timerId;
        },
        sendLocation(location) {
            sendSocketMessage("user location message", {
                siteId: this.site._id,
                userId: this.user._id,
                username: this.user.name,
                conversationId: this.contact.lastConversation._id,
                location,
                whatsappNumber:
                    _.get(this.contact.lastConversation, "whatsappNumber") || _.get(this.contact, "whatsappNumber"),
                telegramUserId:
                    _.get(this.contact.lastConversation, "telegramUserId") || _.get(this.contact, "telegramUserId"),
                linkedinUserId:
                    _.get(this.contact.lastConversation, "linkedinUserId") || _.get(this.contact, "linkedinUserId"),
                channelId: _.get(this.contact.lastConversation, "channel"),
            });
        },
        async sendVoiceMessage(blob) {
            const replyToMessage = Object.values(this.replyToMessages).find(
                (m) => m.raw.visitorId === this.contact._id || m.raw.visitor === this.contact._id
            );
            let path = `${this.site._id}/voice/${this.contact.lastConversation._id}`;
            const mimeType = "audio/mpeg";
            const ext = "mp3";
            let { data } = await uploadAsset({
                path,
                mime: mimeType,
                ext,
                platform: this.contact.lastConversation.ref,
                size: blob.size,
                type: "conversation",
            });
            await axios({
                method: data.method,
                url: data.url,
                data: blob,
                headers: { "Content-Type": mimeType },
            });
            sendSocketMessage("user media message", {
                conversationId: this.contact.lastConversation._id,
                userId: this.user._id,
                username: this.user.name,
                siteId: this.site._id,
                asset: data.asset,
                channelId: _.get(this.contact, "lastConversation.channel", null),
                whatsappNumber:
                    _.get(this.contact, "lastConversation.whatsappNumber", null) ||
                    _.get(this.contact, "whatsappNumber", null),
                instagramUserId:
                    _.get(this.contact, "lastConversation.instagramUserId", null) ||
                    _.get(this.contact, "instagramUserId", null),
                messengerUserId:
                    _.get(this.contact, "lastConversation.messengerUserId", null) ||
                    _.get(this.contact, "messengerUserId", null),
                telegramUserId:
                    _.get(this.contact, "lastConversation.telegramUserId", null) ||
                    _.get(this.contact, "telegramUserId", null),
                linkedinUserId:
                    _.get(this.contact, "lastConversation.linkedinUserId", null) ||
                    _.get(this.contact, "linkedinUserId", null),
                replyToMessage: _.get(replyToMessage, "raw"),
            });

            const conversationId = _.get(
                replyToMessage,
                "raw.conversationId",
                _.get(replyToMessage, "raw.conversation")
            );
            if (conversationId) this.unpinReplyToMessage(conversationId);
            log("sendVoiceMessage", { blob, data });
        },
        handleGoogleDriveFiles(assets) {
            this.showGoogleDrivePicker = false;
            if (assets && assets.length > 0) {
                assets.forEach((asset) => {
                    this.$emit("media-message", { asset });
                });
            }
        },
        handleReplyCommentAsDM() {
            log("handleReplyCommentAsDM triggered");
            this.instagramReplyMode = 'dm';
            // focusEditor watch'ta instagramReplyMode değişince otomatik çağrılacak
        },
        handleReplyComment() {
            log("handleReplyComment triggered");
            this.instagramReplyMode = 'comment';
            // focusEditor watch'ta instagramReplyMode değişince otomatik çağrılacak
        },
        handleToggleHideComment({ commentId, hide }) {
            log("handleToggleHideComment triggered", { commentId, hide });
            const platform = this.contact?.platform;
            
            if (platform === "instagram") {
                sendSocketMessage("meta change-hide-status-of-ig-comment", {
                    siteId: this.site._id,
                    channelId: this.contact.channel,
                    commentId: commentId,
                    hide: hide,
                });
            }
        },
        handleDeleteComment({ commentId }) {
            log("handleDeleteComment triggered", { commentId });
            const platform = this.contact?.platform;
            
            if (platform === "instagram") {
                this.$confirm({
                    title: "Yorumu Sil",
                    content: "Bu yorumu silmek istediğinizden emin misiniz?",
                    okText: "Sil",
                    cancelText: "İptal",
                    onOk: () => {
                        sendSocketMessage("meta delete-ig-comment", {
                            siteId: this.site._id,
                            channelId: this.contact.channel,
                            commentId: commentId,
                        });
                    },
                });
            }
        },
    },
    watch: {
        message(newValue) {
            if (newValue.includes("<span>")) {
                const regex = /<span>|<\/span>/gi;
                const message = newValue.replace(regex, "");
                this.$refs.editor.setContent(message);
            }
            if (newValue.trim().length > 0) {
                if (this.settings.agentTyping) {
                    this.sendTypingInfo();
                }
                if (this.contact.lastConversation) {
                    this.addDraftMessageDelayed(this.contact.lastConversation._id, newValue);
                }
            }
        },
        contact: {
            deep: true,
            handler(newVal, oldVal) {
                if (newVal && newVal?._id !== oldVal?._id) {
                    this.bindDraftMessage(newVal.lastConversation._id);
                    this.instagramReplyMode = null; // Contact değişince mode'u sıfırla
                }
            },
        },
        instagramReplyMode(newVal) {
            // Mode set edildiğinde editor'e focus yap
            if (newVal) {
                this.$nextTick(() => {
                    this.focusEditor();
                });
            }
        },
    },
    beforeDestroy() {
        this.cancelTokens.forEach((fn) => fn());
    },
};
</script>
<style lang="scss">
.message-area {
    border-top: 1px solid var(--border-neutral-default);
    position: relative;

    &-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        position: relative;
    }
}
</style>
